import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save } from 'lucide-react';
import { api } from '@/lib/api';
import type { CinemaFilm } from '@/types';

const filmSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  durationMinutes: z.number().optional(),
  rating: z.string().optional(),
  genre: z.string().optional(),
  posterUrl: z.string().optional(),
  trailerUrl: z.string().optional(),
  releaseDate: z.string().optional(),
  status: z.enum(['coming_soon', 'now_showing', 'ended']),
});

type FilmFormData = z.infer<typeof filmSchema>;

const FILM_RATINGS = ['U', 'PG', '12A', '12', '15', '18', 'TBC'];

const GENRES = [
  'Action',
  'Adventure',
  'Animation',
  'Comedy',
  'Crime',
  'Documentary',
  'Drama',
  'Family',
  'Fantasy',
  'Horror',
  'Musical',
  'Mystery',
  'Romance',
  'Sci-Fi',
  'Thriller',
  'War',
  'Western',
];

export default function FilmForm() {
  const { venueId, filmId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!filmId && filmId !== 'new';

  const { data: existingFilm, isLoading } = useQuery<CinemaFilm>({
    queryKey: ['cinema-film', filmId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/cinema/films/${filmId}`);
      return response.data;
    },
    enabled: isEditing,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<FilmFormData>({
    resolver: zodResolver(filmSchema),
    defaultValues: {
      title: '',
      description: '',
      durationMinutes: undefined,
      rating: '',
      genre: '',
      posterUrl: '',
      trailerUrl: '',
      releaseDate: '',
      status: 'now_showing',
    },
  });

  const posterUrl = watch('posterUrl');

  useEffect(() => {
    if (existingFilm) {
      reset({
        title: existingFilm.title,
        description: existingFilm.description || '',
        durationMinutes: existingFilm.duration_minutes || undefined,
        rating: existingFilm.rating || '',
        genre: existingFilm.genre || '',
        posterUrl: existingFilm.poster_url || '',
        trailerUrl: existingFilm.trailer_url || '',
        releaseDate: existingFilm.release_date?.split('T')[0] || '',
        status: existingFilm.status,
      });
    }
  }, [existingFilm, reset]);

  const saveMutation = useMutation({
    mutationFn: async (data: FilmFormData) => {
      if (isEditing) {
        await api.put(`/admin/venues/${venueId}/cinema/films/${filmId}`, data);
      } else {
        await api.post(`/admin/venues/${venueId}/cinema/films`, data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cinema-films', venueId] });
      navigate(`/venues/${venueId}/cinema/films`);
    },
  });

  const onSubmit = (data: FilmFormData) => {
    saveMutation.mutate(data);
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/cinema/films`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Film' : 'Add Film'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Film Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                {...register('title')}
                className="input"
                placeholder="Film title"
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                {...register('description')}
                className="input"
                rows={3}
                placeholder="Film synopsis"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (minutes)
              </label>
              <input
                type="number"
                {...register('durationMinutes', { valueAsNumber: true })}
                className="input"
                placeholder="e.g., 120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating
              </label>
              <select {...register('rating')} className="input">
                <option value="">Select rating</option>
                {FILM_RATINGS.map((rating) => (
                  <option key={rating} value={rating}>
                    {rating}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Genre
              </label>
              <select {...register('genre')} className="input">
                <option value="">Select genre</option>
                {GENRES.map((genre) => (
                  <option key={genre} value={genre}>
                    {genre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Release Date
              </label>
              <input
                type="date"
                {...register('releaseDate')}
                className="input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select {...register('status')} className="input">
                <option value="coming_soon">Coming Soon</option>
                <option value="now_showing">Now Showing</option>
                <option value="ended">Ended</option>
              </select>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Media</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Poster URL
              </label>
              <input
                type="url"
                {...register('posterUrl')}
                className="input"
                placeholder="https://..."
              />
              {posterUrl && (
                <div className="mt-2">
                  <img
                    src={posterUrl}
                    alt="Poster preview"
                    className="w-32 h-48 object-cover rounded border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trailer URL
              </label>
              <input
                type="url"
                {...register('trailerUrl')}
                className="input"
                placeholder="https://youtube.com/..."
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/venues/${venueId}/cinema/films`)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="btn btn-primary"
          >
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : isEditing ? 'Update Film' : 'Create Film'}
          </button>
        </div>
      </form>
    </div>
  );
}
